import * as React from 'react';
import { type PropsWithChildren } from 'react';
import $ from 'jquery';
import { useLatestRef } from '@epihub/web/src/hooks/useLatestRef';
import { fpSet } from '@epihub/core/src/fn/object';
import { type MessageId } from '@krishanmadan/core/src/types';
import { reactSpringPortal } from '../../lib/HTMLComp';
import { useGlobalStore } from '../../store/useGlobalStore';
import { KeyboardContext } from './KeyboardContext';

export type TAbsoluteOrigin = { x: number; y: number };

export const chatViewAnimationOriginId = 'message-animation-origin-box';

const contextValue = {
  animatingMessageIds: {} as Record<MessageId, boolean>,
  setAnimateMessageById: null as any,
  onAnimateMessageByIdComplete: null as any,
  animationOrigin: {
    x: 0,
    y: 0,
  } as TAbsoluteOrigin,
  chatViewAnimationOriginId,
  chatListScroller: null as (null | ReturnType<typeof useScrollOnAddMessage>),
  recalculateAnimationOrigin: null as any,
};

export const ChatViewMessageBoxAnimationContext = React.createContext({
  ...contextValue,
  chatViewMessageBoxAnimationContextRef: null as any as React.MutableRefObject<typeof contextValue>,
});

type TChatViewMessageBoxAnimationContextProps = {
  //
};

export const ChatViewMessageBoxAnimationContextProvider = (
  { children }: PropsWithChildren<TChatViewMessageBoxAnimationContextProps>,
) => {
  const [animatingMessageIds, setAnimatingMessageIds] = React.useState<Record<MessageId, boolean>>(
    contextValue.animatingMessageIds,
  );
  const [animationOrigin, setAnimationOrigin] = React.useState(contextValue.animationOrigin);

  const defaultAnimationOriginRef = React.useRef<TAbsoluteOrigin>(contextValue.animationOrigin);

  const recalculateAnimationOrigin = React.useCallback(() => {
    const origin = document.getElementById(chatViewAnimationOriginId);
    const originRect = origin?.getBoundingClientRect();
    if (originRect) {
      const newOrigin = {
        x: originRect.x,
        y: originRect.y - originRect.height,
      };
      defaultAnimationOriginRef.current = newOrigin;
      setAnimationOrigin(newOrigin);
    }
  }, []);

  React.useLayoutEffect(() => {
    recalculateAnimationOrigin();
  }, [recalculateAnimationOrigin]);

  const setAnimateMessageById = React.useCallback((messageId: MessageId, origin?: TAbsoluteOrigin) => {
    setAnimationOrigin(origin ?? defaultAnimationOriginRef.current);
    setAnimatingMessageIds((ids) => fpSet(messageId, true, ids));
  }, [setAnimatingMessageIds, setAnimationOrigin, defaultAnimationOriginRef]);

  const onAnimateMessageByIdComplete = React.useCallback((messageId: MessageId) => {
    setAnimatingMessageIds((ids) => fpSet(messageId, false, ids));
  }, [setAnimatingMessageIds]);

  const chatListScroller = useScrollOnAddMessage();
  const chatViewMessageBoxAnimationContextValue = {
    animatingMessageIds,
    setAnimateMessageById,
    onAnimateMessageByIdComplete,
    animationOrigin,
    chatViewAnimationOriginId,
    chatListScroller,
    recalculateAnimationOrigin,
  };

  const chatViewMessageBoxAnimationContextRef = useLatestRef(chatViewMessageBoxAnimationContextValue);

  const { keyboardOpen } = React.useContext(KeyboardContext);
  React.useEffect(() => {
    $(reactSpringPortal).css({
      visibility: keyboardOpen ? 'hidden' : 'visible',
      overflow: keyboardOpen ? 'hidden' : 'visible',
    });
  }, [keyboardOpen]);

  return (
    <ChatViewMessageBoxAnimationContext.Provider
      value={{
        ...chatViewMessageBoxAnimationContextValue,
        chatViewMessageBoxAnimationContextRef,
      }}
    >
      {children}
    </ChatViewMessageBoxAnimationContext.Provider>
  );
};

function useScrollOnAddMessage() {
  const messages = useGlobalStore((store) => store.conversation.conversation.messages);
  const { keyboardOpen, keyboardContextRef } = React.useContext(KeyboardContext);
  const scrollableRefInternal = React.createRef<HTMLDivElement>();
  const scrollableRefExternal = React.createRef<HTMLDivElement>();

  const scrollToEnd = React.useCallback(() => {
    const scrollableRef = keyboardContextRef.current.keyboardOpen
      ? scrollableRefExternal
      : scrollableRefInternal;

    scrollableRef.current?.scrollTo({
      behavior: 'smooth',
      top: scrollableRef.current?.scrollHeight,
    });
  }, [keyboardContextRef, scrollableRefExternal, scrollableRefInternal]);

  React.useEffect(() => {
    scrollToEnd();
  }, [scrollToEnd, messages.length]);

  React.useEffect(() => {
    if (keyboardOpen) {
      scrollToEnd();
    }
  }, [keyboardOpen, scrollToEnd]);

  return {
    scrollableRefInternal,
    scrollableRefExternal,
    scrollToEnd,
  };
}
